import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Plus, Play, Pencil, Trash2, Loader2, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { TaskSchedule } from '@/app/types/taskSchedule';
import { taskScheduleService } from '@/app/services/taskScheduleService';
import TaskScheduleForm from './TaskScheduleForm';

function getPriorityVariant(priority: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (priority) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'default';
    case 'low':
      return 'secondary';
    default:
      return 'outline';
  }
}

function formatFrequency(schedule: TaskSchedule): string {
  const freq = schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1);
  return freq;
}

export default function SchedulesTab({ partyId }: { partyId: number }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<TaskSchedule | null>(null);

  // Fetch schedules for this party
  const { data, isLoading } = useQuery({
    queryKey: ['task-schedules', 'party', partyId],
    queryFn: () => taskScheduleService.listForParty(partyId),
    enabled: Boolean(partyId),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => taskScheduleService.delete(id),
    onSuccess: () => {
      toast.success('Task schedule deleted');
      queryClient.invalidateQueries({ queryKey: ['task-schedules'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete task schedule');
    },
  });

  // Generate tasks mutation
  const generateMutation = useMutation({
    mutationFn: () => taskScheduleService.generate({ dry_run: false }),
    onSuccess: (result) => {
      toast.success(`Generated ${result.tasks_created} tasks from ${result.schedules_processed} schedules`);
      if (result.errors && result.errors.length > 0) {
        result.errors.forEach((err) => toast.error(err));
      }
      queryClient.invalidateQueries({ queryKey: ['task-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to generate tasks');
    },
  });

  const handleCreate = () => {
    setSelectedSchedule(null);
    setShowForm(true);
  };

  const handleEdit = (schedule: TaskSchedule) => {
    setSelectedSchedule(schedule);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedSchedule(null);
  };

  const schedules = data?.items || [];

  return (
    <div className="space-y-4">
      {/* Header with buttons */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Task Schedules</h3>
          <p className="text-sm text-muted-foreground">
            Recurring task schedules for automatic task generation
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
          >
            {generateMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Generate Tasks
          </Button>
          <Button size="sm" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Schedule
          </Button>
        </div>
      </div>

      {/* Table of schedules */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : schedules.length > 0 ? (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Title Template
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Frequency
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Priority
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Active
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Next Due
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Last Generated
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Count
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {schedules.map((schedule) => (
                <tr key={schedule.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <CalendarClock className="h-4 w-4 text-muted-foreground" />
                      {schedule.title_template}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {formatFrequency(schedule)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <Badge variant={getPriorityVariant(schedule.priority)}>
                      {schedule.priority}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <Badge variant={schedule.is_active ? 'default' : 'secondary'}>
                      {schedule.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {schedule.next_due_at
                      ? format(new Date(schedule.next_due_at), 'MMM d, yyyy')
                      : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {schedule.last_generated_at
                      ? format(new Date(schedule.last_generated_at), 'MMM d, yyyy')
                      : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-sm">{schedule.generation_count}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(schedule)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this task schedule? This action
                              cannot be undone. Previously generated tasks will not be affected.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMutation.mutate(schedule.id)}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Alert>
          <AlertDescription>
            No task schedules found for this party. Click "Add Schedule" to create a recurring
            task schedule.
          </AlertDescription>
        </Alert>
      )}

      {/* Form dialog */}
      <TaskScheduleForm
        open={showForm}
        onOpenChange={setShowForm}
        schedule={selectedSchedule}
        partyId={partyId}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
}
